import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button, useNotification } from '../common';
import { FaPlus, FaTrash, FaEdit, FaCheck, FaTimes, FaUndo } from 'react-icons/fa';

// Attribution Rules Manager Component
const AttributionRulesManager = ({ type }) => {
  const { showNotification } = useNotification();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [newRule, setNewRule] = useState({ 
    name: '', 
    sourceContains: '', 
    attributionTarget: '', 
    isActive: true 
  });
  const [showAddForm, setShowAddForm] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  
  // API endpoint depends on type (google or meta)
  const apiEndpoint = type === 'google' 
    ? '/api/google-ads/attribution-rules' 
    : '/api/meta-ads/attribution-rules';
  
  // Labels depending on type
  const labels = {
    title: type === 'google' ? 'Campaign Attribution' : 'Ad Set Attribution',
    target: type === 'google' ? 'Campaign Name' : 'Ad Set Name',
    targetDescription: type === 'google' 
      ? 'The Google campaign name that should get attribution' 
      : 'The Meta ad set name that should get attribution',
  };

  // Load rules and campaigns on component mount
  useEffect(() => {
    fetchRules();
    if (type === 'google') {
      fetchCampaigns();
    }
  }, [type]);

  // Fetch attribution rules from server
  const fetchRules = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(apiEndpoint, {
        headers: { 'x-auth-token': token }
      });
      setRules(response.data);
    } catch (error) {
      console.error(`Error fetching ${type} attribution rules:`, error);
      showNotification(`Failed to load attribution rules`, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch campaigns from KPIs API
  const fetchCampaigns = async () => {
    setLoadingCampaigns(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/kpis?period=last_30_days', {
        headers: { 'x-auth-token': token }
      });
      
      // Extract Google campaigns from the KPIs data
      const googleCampaigns = [];
      
      // Get campaigns from ad spend data
      if (response.data.kpiData.adSpend?.current?.byCampaign) {
        Object.keys(response.data.kpiData.adSpend.current.byCampaign).forEach(campaign => {
          if (response.data.kpiData.adSpend.current.byCampaign[campaign].source === 'google') {
            googleCampaigns.push(campaign);
          }
        });
      }
      
      // Get campaigns from ROAS data if available
      if (response.data.kpiData.roas?.bySource?.google?.campaigns) {
        Object.keys(response.data.kpiData.roas.bySource.google.campaigns).forEach(campaign => {
          if (!googleCampaigns.includes(campaign)) {
            googleCampaigns.push(campaign);
          }
        });
      }
      
      setCampaigns(googleCampaigns.sort());
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      showNotification('Failed to load campaigns', 'error');
    } finally {
      setLoadingCampaigns(false);
    }
  };

  // Save new rule
  const handleSaveRule = async () => {
    if (!newRule.name || !newRule.sourceContains || !newRule.attributionTarget) {
      showNotification('All fields are required', 'error');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(apiEndpoint, {
        name: newRule.name,
        sourceContains: newRule.sourceContains,
        attributionTarget: newRule.attributionTarget,
        isActive: newRule.isActive
      }, {
        headers: { 'x-auth-token': token }
      });
      
      showNotification('Attribution rule saved successfully', 'success');
      setNewRule({ name: '', sourceContains: '', attributionTarget: '', isActive: true });
      setShowAddForm(false);
      fetchRules();
    } catch (error) {
      console.error(`Error saving attribution rule:`, error);
      showNotification('Failed to save attribution rule', 'error');
    }
  };

  // Update existing rule
  const handleUpdateRule = async (ruleId) => {
    if (!editingRule.name || !editingRule.sourceContains || !editingRule.attributionTarget) {
      showNotification('All fields are required', 'error');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${apiEndpoint}/${ruleId}`, {
        name: editingRule.name,
        sourceContains: editingRule.sourceContains,
        attributionTarget: editingRule.attributionTarget,
        isActive: editingRule.isActive
      }, {
        headers: { 'x-auth-token': token }
      });
      
      showNotification('Attribution rule updated successfully', 'success');
      setEditingRule(null);
      fetchRules();
    } catch (error) {
      console.error(`Error updating attribution rule:`, error);
      showNotification('Failed to update attribution rule', 'error');
    }
  };

  // Delete rule
  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm('Are you sure you want to delete this attribution rule?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${apiEndpoint}/${ruleId}`, {
        headers: { 'x-auth-token': token }
      });
      
      showNotification('Attribution rule deleted successfully', 'success');
      fetchRules();
    } catch (error) {
      console.error(`Error deleting attribution rule:`, error);
      showNotification('Failed to delete attribution rule', 'error');
    }
  };

  // Toggle rule active status
  const handleToggleActive = async (rule) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${apiEndpoint}/${rule.id}`, {
        isActive: !rule.is_active
      }, {
        headers: { 'x-auth-token': token }
      });
      
      showNotification(`Rule ${!rule.is_active ? 'activated' : 'deactivated'} successfully`, 'success');
      fetchRules();
    } catch (error) {
      console.error(`Error toggling rule status:`, error);
      showNotification('Failed to update rule status', 'error');
    }
  };
  
  // Start editing a rule
  const startEditing = (rule) => {
    setEditingRule({
      id: rule.id,
      name: rule.name,
      sourceContains: rule.source_contains,
      attributionTarget: rule.attribution_target,
      isActive: rule.is_active
    });
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingRule(null);
  };

  // Handle input change for new rule form
  const handleNewRuleChange = (e) => {
    const { name, value, checked, type: inputType } = e.target;
    setNewRule({
      ...newRule,
      [name]: inputType === 'checkbox' ? checked : value
    });
  };

  // Handle input change for editing form
  const handleEditRuleChange = (e) => {
    const { name, value, checked, type: inputType } = e.target;
    setEditingRule({
      ...editingRule,
      [name]: inputType === 'checkbox' ? checked : value
    });
  };

  return (
    <div className="bg-white border border-gray-200 rounded-md">
      {/* Rules list table */}
      <div className="overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Latest Source Contains</th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{labels.target}</th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan="5" className="px-4 py-3 text-center text-gray-500">
                  Loading rules...
                </td>
              </tr>
            ) : rules.length === 0 && !showAddForm ? (
              <tr>
                <td colSpan="5" className="px-4 py-3 text-center text-gray-500">
                  No attribution rules found. Add your first rule to track campaign/ad set ROAS.
                </td>
              </tr>
            ) : (
              <>
                {rules.map(rule => (
                  <tr key={rule.id} className={!rule.is_active ? 'bg-gray-50' : ''}>
                    {editingRule && editingRule.id === rule.id ? (
                      // Editing row
                      <>
                        <td className="px-4 py-2">
                          <input
                            type="text"
                            name="name"
                            value={editingRule.name}
                            onChange={handleEditRuleChange}
                            placeholder="Rule name"
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </td>
                        <td className="px-4 py-2">
                          <input
                            type="text"
                            name="sourceContains"
                            value={editingRule.sourceContains}
                            onChange={handleEditRuleChange}
                            placeholder="e.g., gclid (in latest source)"
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </td>
                        <td className="px-4 py-2">
                          {type === 'google' ? (
                            <select
                              name="attributionTarget"
                              value={editingRule.attributionTarget}
                              onChange={handleEditRuleChange}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                              disabled={loadingCampaigns}
                            >
                              <option value="">
                                {loadingCampaigns ? 'Loading campaigns...' : 'Select campaign'}
                              </option>
                              {campaigns.map(campaign => (
                                <option key={campaign} value={campaign}>
                                  {campaign}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type="text"
                              name="attributionTarget"
                              value={editingRule.attributionTarget}
                              onChange={handleEditRuleChange}
                              placeholder="Ad set name"
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                          )}
                        </td>
                        <td className="px-4 py-2">
                          <label className="inline-flex items-center">
                            <input 
                              type="checkbox" 
                              name="isActive"
                              checked={editingRule.isActive}
                              onChange={handleEditRuleChange}
                              className="form-checkbox h-4 w-4 text-blue-600" 
                            />
                            <span className="ml-1 text-sm text-gray-700">Active</span>
                          </label>
                        </td>
                        <td className="px-4 py-2 text-right text-sm font-medium space-x-2">
                          <button 
                            type="button"
                            onClick={() => handleUpdateRule(rule.id)}
                            className="text-green-600 hover:text-green-900"
                          >
                            <FaCheck />
                          </button>
                          <button 
                            type="button"
                            onClick={cancelEditing}
                            className="text-red-600 hover:text-red-900"
                          >
                            <FaTimes />
                          </button>
                        </td>
                      </>
                    ) : (
                      // Normal row display
                      <>
                        <td className="px-4 py-3 text-sm text-gray-900">{rule.name}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono">{rule.source_contains}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{rule.attribution_target}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            rule.is_active 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {rule.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-medium space-x-2">
                          <button 
                            type="button"
                            onClick={() => handleToggleActive(rule)}
                            className={`${rule.is_active ? 'text-red-500 hover:text-red-700' : 'text-green-500 hover:text-green-700'}`}
                            title={rule.is_active ? 'Deactivate' : 'Activate'}
                          >
                            {rule.is_active ? <FaTimes /> : <FaCheck />}
                          </button>
                          <button 
                            type="button"
                            onClick={() => startEditing(rule)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Edit"
                          >
                            <FaEdit />
                          </button>
                          <button 
                            type="button"
                            onClick={() => handleDeleteRule(rule.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete"
                          >
                            <FaTrash />
                          </button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
                
                {/* Add new rule form row */}
                {showAddForm && (
                  <tr className="bg-blue-50">
                    <td className="px-4 py-2">
                      <input
                        type="text"
                        name="name"
                        value={newRule.name}
                        onChange={handleNewRuleChange}
                        placeholder="Rule name"
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="text"
                        name="sourceContains"
                        value={newRule.sourceContains}
                        onChange={handleNewRuleChange}
                        placeholder="e.g., gclid (in latest source)"
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    </td>
                    <td className="px-4 py-2">
                      {type === 'google' ? (
                        <select
                          name="attributionTarget"
                          value={newRule.attributionTarget}
                          onChange={handleNewRuleChange}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          disabled={loadingCampaigns}
                        >
                          <option value="">
                            {loadingCampaigns ? 'Loading campaigns...' : 'Select campaign'}
                          </option>
                          {campaigns.map(campaign => (
                            <option key={campaign} value={campaign}>
                              {campaign}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          name="attributionTarget"
                          value={newRule.attributionTarget}
                          onChange={handleNewRuleChange}
                          placeholder="Ad set name"
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <label className="inline-flex items-center">
                        <input 
                          type="checkbox" 
                          name="isActive"
                          checked={newRule.isActive}
                          onChange={handleNewRuleChange}
                          className="form-checkbox h-4 w-4 text-blue-600" 
                        />
                        <span className="ml-1 text-sm text-gray-700">Active</span>
                      </label>
                    </td>
                    <td className="px-4 py-2 text-right text-sm font-medium space-x-2">
                      <button 
                        type="button"
                        onClick={handleSaveRule}
                        className="text-green-600 hover:text-green-900"
                      >
                        <FaCheck />
                      </button>
                      <button 
                        type="button"
                        onClick={() => setShowAddForm(false)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <FaTimes />
                      </button>
                    </td>
                  </tr>
                )}
              </>
            )}
          </tbody>
        </table>
      </div>
      
      {/* Add new rule button */}
      {!showAddForm && (
        <div className="px-4 py-3 bg-gray-50 text-right">
          <Button
            variant="primary"
            onClick={() => setShowAddForm(true)}
            className="inline-flex items-center py-1"
          >
            <FaPlus className="mr-1" /> Add Rule
          </Button>
        </div>
      )}
      
      {/* Help information */}
      <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
        <p><strong>Source Contains:</strong> Text pattern to look for in lead's <em>latest source</em> data (e.g., campaign ID, UTM parameter)</p>
        <p><strong>{labels.target}:</strong> {labels.targetDescription}</p>
        <p className="mt-1">When a lead's latest source matches the pattern, the revenue from any deals created from this lead will be attributed to the specified campaign/ad set for ROAS calculation.</p>
      </div>
    </div>
  );
};

const GoogleAdsSettings = () => {
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(true);
  const [fetchingData, setFetchingData] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasCredentials, setHasCredentials] = useState(false);
  const [credentials, setCredentials] = useState({
    clientId: '',
    clientSecret: '',
    developerToken: '',
    refreshToken: '',
    customerId: ''
  });
  const [maskedCredentials, setMaskedCredentials] = useState({
    clientId: '',
    clientSecret: '••••••••',
    developerToken: '••••••••',
    refreshToken: '••••••••',
    customerId: '',
    updatedAt: null
  });
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [fetchResult, setFetchResult] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchCredentials();
    
    // Set default date range to last 30 days
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);
    
    setEndDate(today.toISOString().split('T')[0]);
    setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
  }, []);

  const fetchCredentials = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/google-ads/credentials', {
        headers: { 'x-auth-token': token }
      });
      
      
      if (response.data.hasCredentials) {
        setHasCredentials(true);
        
        // Set masked credentials for display
        setMaskedCredentials({
          clientId: response.data.clientId || '',
          clientSecret: response.data.clientSecret || '••••••••',
          developerToken: response.data.developerToken || '••••••••',
          refreshToken: response.data.refreshToken || '••••••••',
          customerId: response.data.customerId || '',
          updatedAt: response.data.updatedAt
        });
        
        // Also update the credentials input field with the current customerId
        setCredentials(prev => ({
          ...prev,
          customerId: response.data.customerId || ''
        }));
      } else {
        setHasCredentials(false);
      }
    } catch (error) {
      console.error('Error fetching Google Ads credentials:', error);
      setError('Failed to fetch Google Ads credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCredentials({
      ...credentials,
      [name]: value
    });
  };

  const saveCredentials = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      
      // Check if we need to update all credentials or just customerId
      // If any field has a value, include it in the update
      const dataToSave = {
        ...(credentials.clientId ? { clientId: credentials.clientId } : {}),
        ...(credentials.clientSecret ? { clientSecret: credentials.clientSecret } : {}),
        ...(credentials.developerToken ? { developerToken: credentials.developerToken } : {}),
        ...(credentials.refreshToken ? { refreshToken: credentials.refreshToken } : {}),
        customerId: credentials.customerId
      };
      
      
      await axios.post('/api/google-ads/credentials', dataToSave, {
        headers: { 'x-auth-token': token }
      });
      
      showNotification('Google Ads credentials saved successfully', 'success');
      await fetchCredentials();
    } catch (error) {
      console.error('Error saving Google Ads credentials:', error);
      setError('Failed to save Google Ads credentials');
      showNotification('Failed to save Google Ads credentials', 'error');
    } finally {
      setSaving(false);
    }
  };

  const fetchAdSpendData = async (e) => {
    e.preventDefault();
    setFetchingData(true);
    setError('');
    setFetchResult(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/google-ads/fetch-ad-spend', 
        { startDate, endDate },
        { headers: { 'x-auth-token': token } }
      );
      
      setFetchResult(response.data);
      showNotification(`Successfully fetched and stored ${response.data.count} ad spend records`, 'success');
    } catch (error) {
      console.error('Error fetching ad spend data:', error);
      setError(error.response?.data?.message || 'Failed to fetch ad spend data');
      showNotification('Failed to fetch ad spend data', 'error');
    } finally {
      setFetchingData(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <div>
      <h3 className="text-lg font-medium text-gray-900 mb-4">Google Ads Integration</h3>
      
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      <div className="mb-6">
        <p className="text-sm text-gray-600 mb-4">
          Configure your Google Ads API credentials to track ad spend in the KPIs dashboard.
        </p>
        
        {hasCredentials && (
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
            <h4 className="font-medium text-blue-800 mb-2">Current Credentials</h4>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <div className="text-gray-600">Client ID:</div>
              <div className="font-medium">{maskedCredentials.clientId}</div>
              
              <div className="text-gray-600">Client Secret:</div>
              <div className="font-medium">{maskedCredentials.clientSecret}</div>
              
              <div className="text-gray-600">Developer Token:</div>
              <div className="font-medium">{maskedCredentials.developerToken}</div>
              
              <div className="text-gray-600">Refresh Token:</div>
              <div className="font-medium">{maskedCredentials.refreshToken || 'Not set'}</div>
              
              <div className="text-gray-600">Customer ID:</div>
              <div className="font-medium">{maskedCredentials.customerId || 'Not set'}</div>
              
              <div className="text-gray-600">Last Updated:</div>
              <div className="font-medium">{formatDate(maskedCredentials.updatedAt)}</div>
            </div>
            
          </div>
        )}
        
        <form onSubmit={saveCredentials} className="space-y-4 bg-white rounded-md border border-gray-200 p-4">
          <h4 className="font-medium text-gray-800 mb-2">{hasCredentials ? 'Update Credentials' : 'Add Credentials'}</h4>
          
          <div>
            <label htmlFor="clientId" className="block text-sm font-medium text-gray-700 mb-1">Client ID <span className="text-red-500">*</span></label>
            <input
              type="text"
              id="clientId"
              name="clientId"
              value={credentials.clientId}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Your Google Ads API Client ID"
              required={!hasCredentials}
            />
          </div>
          
          <div>
            <label htmlFor="clientSecret" className="block text-sm font-medium text-gray-700 mb-1">Client Secret <span className="text-red-500">*</span></label>
            <input
              type="password"
              id="clientSecret"
              name="clientSecret"
              value={credentials.clientSecret}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Your Google Ads API Client Secret"
              required={!hasCredentials}
            />
          </div>
          
          <div>
            <label htmlFor="developerToken" className="block text-sm font-medium text-gray-700 mb-1">Developer Token <span className="text-red-500">*</span></label>
            <input
              type="password"
              id="developerToken"
              name="developerToken"
              value={credentials.developerToken}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Your Google Ads API Developer Token"
              required={!hasCredentials}
            />
          </div>
          
          <div>
            <label htmlFor="refreshToken" className="block text-sm font-medium text-gray-700 mb-1">Refresh Token</label>
            <input
              type="password"
              id="refreshToken"
              name="refreshToken"
              value={credentials.refreshToken}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Your OAuth Refresh Token (optional)"
            />
          </div>
          
          <div>
            <label htmlFor="customerId" className="block text-sm font-medium text-gray-700 mb-1">Customer ID <span className="text-red-500">*</span></label>
            <input
              type="text"
              id="customerId"
              name="customerId"
              value={credentials.customerId}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Your Google Ads Customer ID (no dashes)"
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              Find this in Google Ads under Account Settings (format: 123-456-7890 or 1234567890)
            </p>
          </div>
          
          <div className="pt-2">
            <Button
              type="submit"
              variant="primary"
              disabled={saving}
              isLoading={saving}
            >
              {saving ? 'Saving...' : hasCredentials ? 'Update Credentials' : 'Save Credentials'}
            </Button>
          </div>
        </form>
      </div>
      
      {hasCredentials && (
        <div className="mt-8 border-t border-gray-200 pt-6">
          <h4 className="font-medium text-gray-800 mb-4">Fetch Ad Spend Data</h4>
          <p className="text-sm text-gray-600 mb-4">
            Use this form to manually fetch Google Ads spend data for a specific date range.
            The data will be stored in the database and available in the KPIs dashboard.
          </p>
          
          <form onSubmit={fetchAdSpendData} className="space-y-4 bg-white rounded-md border border-gray-200 p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input
                  type="date"
                  id="startDate"
                  name="startDate"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <input
                  type="date"
                  id="endDate"
                  name="endDate"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
            </div>
            
            <div className="pt-2">
              <Button
                type="submit"
                variant="success"
                disabled={fetchingData}
                isLoading={fetchingData}
              >
                {fetchingData ? 'Fetching...' : 'Fetch Ad Spend Data'}
              </Button>
            </div>
            
            {fetchResult && (
              <div className="mt-4 bg-green-50 border border-green-200 rounded-md p-3 text-sm text-green-800">
                <p className="font-medium">{fetchResult.message}</p>
                <p>Number of records: {fetchResult.count}</p>
              </div>
            )}
          </form>
        </div>
      )}
      
      {/* Campaign Attribution Rules */}
      {hasCredentials && (
        <div className="mt-8 border-t border-gray-200 pt-6">
          <h4 className="font-medium text-gray-800 mb-4">Campaign Attribution Rules</h4>
          <p className="text-sm text-gray-600 mb-4">
            Set up rules to attribute revenue from deals to specific Google campaigns based on lead source data. 
            These rules help calculate ROAS at the campaign level in the KPIs dashboard.
          </p>
          
          <AttributionRulesManager type="google" />
        </div>
      )}
      
      <div className="mt-8 border-t border-gray-200 pt-6">
        <h4 className="font-medium text-gray-800 mb-4">Integration Information</h4>
        <div className="bg-gray-50 rounded-md p-4 text-sm">
          <p className="mb-2">To use this integration, you need to set up the following:</p>
          <ol className="list-decimal list-inside space-y-2 ml-2">
            <li>Create a project in the <a href="https://console.developers.google.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Google Cloud Console</a></li>
            <li>Enable the Google Ads API</li>
            <li>Create OAuth 2.0 credentials (Client ID and Client Secret)</li>
            <li>Apply for a Developer Token from Google Ads</li>
            <li>Generate a refresh token using your OAuth credentials</li>
          </ol>
          <p className="mt-4 text-gray-600">
            For more information, refer to the <a href="https://developers.google.com/google-ads/api/docs/oauth/overview" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Google Ads API documentation</a>.
          </p>
        </div>
      </div>
    </div>
  );
};

export default GoogleAdsSettings;